import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./HandoffPaywall.module.scss"

/**
 * HandoffPaywall (`/handoff`) — an original, self-contained NON-ONBOARDING
 * paywall for Private Photo Vault Premium. Paywall type rolled at random for
 * this run: NON-ONBOARDING (an existing free user being upsold, no first-run
 * flow around it).
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run was `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority. The entire concept is built to it: this screen is about
 * one thing only — the privacy of the memories on your phone.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "You handed your phone over. They kept swiping." The single most universally
 * felt photo-privacy moment there is: you unlock your phone to show someone ONE
 * picture — the waiter, your kid, a friend, a stranger at a party — and their
 * thumb keeps going. The whole security cluster before this leaned on a LOCK, a
 * GATE, a WATCHER, or a raid. This screen leans on ABSENCE: the safest photo is
 * the one that was never in the Camera Roll to swipe onto. Private Photo Vault
 * MOVES the photos you choose out of the Camera Roll / Recents / Shared and into
 * a locked Vault — so when your phone leaves your hand, there is simply nothing
 * private left in the roll to stumble across.
 *
 * ── The interactive proof (you drive the borrower's thumb) ─────────────────
 * The stage is a phone you've handed over, open on the Camera Roll. YOU swipe it
 * forward, as the person holding it would. One master switch — "Keep private
 * photos in the Vault" — flips the outcome on the SAME roll:
 *   • Vault OFF: the private memories sit inline in the roll; one more swipe
 *     lands on one — "That one wasn't for them. It was right there."
 *   • Vault ON (default, recommended): those memories have lifted out of the
 *     roll into the Vault; the borrower swipes to a clean End of Camera Roll —
 *     "Nothing here that wasn't for them."
 * Value frame is RISK REVERSAL BY ABSENCE, not defense: you don't out-lock the
 * intruder, you leave nothing to find.
 *
 * ── Divergence (vs. the 90+ set, incl. the security cluster) ────────────────
 * Layout      : a handed-over phone open on a Camera Roll photo VIEWER with a
 *               live thumbnail filmstrip — not a vault door, keypad, dial, gate,
 *               two-bolt lock, night-watch ledger, wall, or consolidation grid.
 * Interaction : you SWIPE the borrower's thumb laterally through the roll and
 *               flip one Vault switch — a new verb to the set (browse-as-someone-
 *               else), not arm / dial / tap-a-code / sweep-into-a-pile.
 * Metaphor    : safety by ABSENCE / "nothing to find" — the private photos are
 *               simply not in the roll — vs. a stronger lock or a caught intruder.
 * Motion      : the signature beat is private tiles LIFTING out of the filmstrip
 *               and arcing into the Vault as the switch goes on (the gap closes,
 *               the Vault count ticks up); and, with the Vault off, the dreaded
 *               "one more swipe" landing on a private frame.
 * Value frame : risk reversal by absence + the relief of an unattended phone —
 *               not fear (Caught), engineering (Twofold), or tidying (Unseen).
 * Register    : the easy exhale of handing your phone over without a second
 *               thought. Calm, a little human, quietly reassuring.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * Moving photos into the Vault removing them from the Camera Roll / Recents /
 * Shared is a REAL Private Photo Vault behaviour. The borrower is clearly an
 * EXAMPLE ("you hand it over"), never a claim the user has been targeted.
 * Photos are MOVED, never deleted — the screen says so plainly ("Safe, not
 * gone" · encrypted Cloud Vault backup); nothing here implies data loss on
 * expiration. No countdown, no fake scarcity. "Save {n}%" is real arithmetic
 * from live product data. Yearly is the featured, default, most-prominent price;
 * monthly is small and secondary; the 7-day free trial is the real trial.
 * Renders on built-in debug product data with bundled photos — no native
 * injection, no external egress. Honours reduced motion (arrives resolved, no
 * looping animation).
 */

// A memory in the roll. `priv` tiles are the ones you'd never hand over — the
// ones the Vault keeps out of the Camera Roll. Bundled, self-contained photos
// reused from the repo (byheart set) — nothing explicit; "private" is framing.
type Frame = { src: string; priv: boolean; cap: string }

const ROLL: Frame[] = [
  { src: "/byheart/m12.jpg", priv: false, cap: "The hike, up top" },
  { src: "/byheart/m13.jpg", priv: false, cap: "Bubbles in the yard" },
  { src: "/byheart/m08.jpg", priv: true, cap: "Just for you" },
  { src: "/byheart/m05.jpg", priv: false, cap: "Golden hour" },
  { src: "/byheart/m17.jpg", priv: true, cap: "Just for you" },
  { src: "/byheart/m14.jpg", priv: false, cap: "Campfire, late" },
]

// Inline, currentColor icons so the feature marks render in brand blue and stay
// crisp in both themes (the bundled feature SVGs carry their own fills).
const IconOutOfRoll = (p: any) => (
  <svg viewBox="0 0 24 24" fill="none" {...p}>
    <rect
      x="2.6"
      y="6.4"
      width="12.4"
      height="11.2"
      rx="2.2"
      stroke="currentColor"
      strokeWidth="1.7"
    />
    <circle cx="6.6" cy="10.2" r="1.25" fill="currentColor" />
    <path
      d="M3 15.4l3.4-3 2.6 2.1"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <path
      d="M18 4.2v6.6m0 0l-2.4-2.4M18 10.8l2.4-2.4"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)
const IconOnlyYou = (p: any) => (
  <svg viewBox="0 0 24 24" fill="none" {...p}>
    <rect
      x="4"
      y="10.4"
      width="16"
      height="10.4"
      rx="2.6"
      stroke="currentColor"
      strokeWidth="1.7"
    />
    <path
      d="M7.6 10.4V8a4.4 4.4 0 0 1 8.8 0v2.4"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    <circle cx="12" cy="14.6" r="1.9" stroke="currentColor" strokeWidth="1.7" />
    <path
      d="M12 16.5v2"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
  </svg>
)
const IconSafe = (p: any) => (
  <svg viewBox="0 0 24 24" fill="none" {...p}>
    <path
      d="M7.5 18.5a4 4 0 0 1-.4-8 5.2 5.2 0 0 1 10 .9 3.6 3.6 0 0 1-.6 7.1H7.5z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <path
      d="M9.6 14.1l1.8 1.8 3.2-3.4"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

// The three real features that make the concept true, in support of the one
// idea: your private photos are not in the roll to find.
const FEATURES = [
  {
    key: "outofroll",
    Icon: IconOutOfRoll,
    name: "Out of the Camera Roll",
    line: "Photos you move leave Recents, Shared, and the roll itself — there’s nothing to swipe onto.",
  },
  {
    key: "onlyyou",
    Icon: IconOnlyYou,
    name: "Opens only for you",
    line: "The Vault stays locked behind Face ID and your passcode — even while the rest of your phone is unlocked.",
  },
  {
    key: "safe",
    Icon: IconSafe,
    name: "Safe, not gone",
    line: "Every photo is kept — encrypted in your Cloud Vault and back in a tap. Moved, never deleted.",
  },
]

const HandoffPaywall = () => {
  const { state, actions } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  // Warm the safe-area insets to the stage backdrop so the screen reads as one
  // continuous surface top-to-bottom.
  useEffect(() => {
    const dark = state.initialData?.darkMode
    actions.setHeaderInsetColor(dark ? "#12161f" : "#e9edf4")
    actions.setFooterInsetColor(dark ? "#12161f" : "#e9edf4")
  }, [actions, state.initialData?.darkMode])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── the handoff state machine ─────────────────────────────────────────────
  // vaulted = private photos live in the Vault (safe, default). pos = which
  // frame of the ACTIVE roll the borrower is looking at; END is one past the
  // last frame ("End of Camera Roll"). hint = the one-time swipe teach.
  const [vaulted, setVaulted] = useState(true)
  const [pos, setPos] = useState(0)
  const [hint, setHint] = useState(false)
  const [lifting, setLifting] = useState(false) // private tiles arcing to Vault

  // The active roll: with the Vault on, private frames are simply not in it.
  const activeRoll = useMemo(
    () => (vaulted ? ROLL.filter((f) => !f.priv) : ROLL),
    [vaulted],
  )
  const END = activeRoll.length
  const vaultCount = ROLL.filter((f) => f.priv).length

  // Keep the viewer position valid whenever the roll length changes.
  useEffect(() => {
    setPos((p) => Math.min(p, END))
  }, [END])

  const timers = useRef<number[]>([])
  const clearTimers = () => {
    timers.current.forEach((t) => window.clearTimeout(t))
    timers.current = []
  }
  const after = (ms: number, fn: () => void) =>
    timers.current.push(window.setTimeout(fn, ms))

  // One-time thumb hint on mount to teach the swipe (skipped for reduced motion).
  useEffect(() => {
    if (reduceMotion) return
    const t = window.setTimeout(() => {
      setHint(true)
      window.setTimeout(() => setHint(false), 1700)
    }, 650)
    return () => window.clearTimeout(t)
  }, [reduceMotion])

  useEffect(() => clearTimers, [])

  const current = pos < END ? activeRoll[pos] : null
  const atEnd = pos >= END
  const exposed = !!current && current.priv // only possible with the Vault off
  const canSwipe = pos < END

  const swipe = useCallback(() => {
    setHint(false)
    setPos((p) => (p < END ? p + 1 : p))
  }, [END])

  const rewind = useCallback(() => setPos(0), [])

  // Flip the Vault. Going ON plays the "lift into the Vault" beat; either way we
  // send the borrower back to the start of the (now different) roll so the
  // contrast is clean.
  const toggleVault = useCallback(() => {
    clearTimers()
    setHint(false)
    setVaulted((v) => {
      const next = !v
      if (next && !reduceMotion) {
        setLifting(true)
        after(560, () => setLifting(false))
      }
      return next
    })
    setPos(0)
  }, [reduceMotion])

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  // Human status under the phone, derived from the resolved state.
  const status = (() => {
    if (vaulted) {
      return atEnd
        ? "End of the Camera Roll. Nothing here that wasn’t for them."
        : "Only the photos you’d hand over. The private ones aren’t in the roll."
    }
    if (exposed)
      return "That one wasn’t for them. It was right there in the roll."
    if (atEnd)
      return "End of the roll — but two private photos were sitting in it."
    return "One more swipe…"
  })()

  const rootCls = [
    styles.root,
    vaulted ? styles.safe : styles.open,
    exposed ? styles.exposed : "",
    atEnd ? styles.ended : "",
    lifting ? styles.lifting : "",
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <div className={rootCls}>
      <span className={styles.aura} aria-hidden="true" />
      <div className={styles.scroll}>
        {/* ── header ─────────────────────────────────────────────────────── */}
        <header className={styles.top}>
          <div className={styles.brand}>
            <span className={styles.brandMark} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="14" height="14">
                <rect
                  x="4.2"
                  y="6.5"
                  width="15.6"
                  height="12.4"
                  rx="2.6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />
                <circle
                  cx="12"
                  cy="12.7"
                  r="2.9"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />
                <path
                  d="M9 6.5l1.2-2.1h3.6L15 6.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className={styles.brandName}>
              Private Photo Vault
              <span className={styles.brandTag}>Premium</span>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={() => native.postNativeEvent("close")}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        {/* ── intro ──────────────────────────────────────────────────────── */}
        <section className={styles.intro}>
          <span className={styles.kicker}>
            <span className={styles.kickerDot} aria-hidden="true" />
            When your phone leaves your hand
          </span>
          <h1 className={styles.title}>
            You handed it over.
            <span className={styles.titleAccent}> They kept swiping.</span>
          </h1>
          <p className={styles.sub}>
            You unlock your phone to show one photo — and their thumb keeps
            going. Keep the private ones in the Vault and there’s simply nothing
            in the roll to swipe onto.
          </p>
        </section>

        {/* ── the phone you handed over, open on the Camera Roll ─────────── */}
        <section className={styles.stage}>
          <div className={styles.phone}>
            {/* borrower chip — whose hands this is in right now */}
            <div className={styles.handoff} aria-hidden="true">
              <span className={styles.hands}>
                <svg viewBox="0 0 24 24" width="15" height="15">
                  <path
                    d="M7 13.5V8.2a1.3 1.3 0 0 1 2.6 0v3.3m0-.4V6.6a1.3 1.3 0 0 1 2.6 0v4.1m0-.3V7.1a1.3 1.3 0 0 1 2.6 0v3.9m0-.6a1.3 1.3 0 0 1 2.6 0v4.2c0 2.8-2 5.1-5 5.1-2 0-3-0.7-4.2-2.2l-2.2-2.9a1.3 1.3 0 0 1 2-1.6l1 1.1"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className={styles.handoffText}>
                In someone else’s hands
              </span>
            </div>

            <div className={styles.screen}>
              <div className={styles.screenBar}>
                <span className={styles.screenTitle}>
                  <svg viewBox="0 0 24 24" width="13" height="13">
                    <rect
                      x="3.5"
                      y="5.5"
                      width="17"
                      height="13"
                      rx="2.4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                    />
                    <circle cx="8.4" cy="10" r="1.4" fill="currentColor" />
                    <path
                      d="M4 16.5l4.6-4 3.2 2.6 3-2.4 5.7 4.8"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Camera Roll
                </span>
                <span className={styles.screenCount}>
                  {atEnd ? `${END} of ${END}` : `${pos + 1} of ${END}`}
                </span>
              </div>

              {/* the viewer — one big frame the borrower is looking at */}
              <div className={styles.viewer}>
                <div className={styles.stack}>
                  {current ? (
                    <div
                      className={`${styles.frame} ${
                        current.priv ? styles.framePriv : ""
                      }`}
                      key={`${vaulted}-${pos}`}
                      onClick={swipe}
                      role="img"
                      aria-label={current.cap}
                    >
                      <span
                        className={styles.frameImg}
                        style={{ backgroundImage: `url(${current.src})` }}
                      />
                      {current.priv ? (
                        <>
                          <span className={styles.privScrim} />
                          <span className={styles.expRing} aria-hidden="true" />
                          <span className={styles.privTag}>
                            <svg viewBox="0 0 24 24" width="11" height="11">
                              <rect
                                x="5"
                                y="10.6"
                                width="14"
                                height="8.4"
                                rx="2"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                              />
                              <path
                                d="M8 10.6V8.4a4 4 0 0 1 8 0v2.2"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                              />
                            </svg>
                            Private
                          </span>
                          <span className={styles.seenTag}>Seen by them</span>
                        </>
                      ) : (
                        <span className={styles.frameCap}>{current.cap}</span>
                      )}
                    </div>
                  ) : (
                    <div className={styles.endCard} key="end">
                      <span className={styles.endMark} aria-hidden="true">
                        {vaulted ? (
                          <svg viewBox="0 0 24 24" width="26" height="26">
                            <path
                              d="M5 12.5l4.4 4.4L19 7.4"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.4"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" width="26" height="26">
                            <path
                              d="M12 4.6l8.4 14.6H3.6L12 4.6z"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.1"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M12 10v4.1"
                              stroke="currentColor"
                              strokeWidth="2.1"
                              strokeLinecap="round"
                            />
                            <circle
                              cx="12"
                              cy="16.7"
                              r="1.15"
                              fill="currentColor"
                            />
                          </svg>
                        )}
                      </span>
                      <span className={styles.endTitle}>
                        {vaulted ? "End of the Camera Roll" : "End of the roll"}
                      </span>
                      <span className={styles.endSub}>
                        {vaulted
                          ? "Nothing here that wasn’t for them."
                          : "…but two private photos were in it."}
                      </span>
                    </div>
                  )}

                  {/* the swipe hint thumb (one-time teach) */}
                  {hint && canSwipe && (
                    <span className={styles.thumb} aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="24" height="24">
                        <path
                          d="M9 11V5.6a1.5 1.5 0 0 1 3 0V11m0-1.2a1.5 1.5 0 0 1 3 0v1.4m0-1a1.5 1.5 0 0 1 3 0V15c0 3-2 5-5 5-1.8 0-3-.7-4.2-2.2l-2.3-3a1.5 1.5 0 0 1 2.3-1.9l1 1"
                          fill="var(--hf-screen)"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  )}
                </div>

                {/* swipe forward affordance */}
                <button
                  className={styles.next}
                  onClick={swipe}
                  disabled={!canSwipe}
                  aria-label="Swipe to the next photo"
                >
                  <svg viewBox="0 0 24 24" width="20" height="20">
                    <path
                      d="M9 5l7 7-7 7"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>

              {/* the filmstrip — the roll itself; private tiles lift out to Vault */}
              <div className={styles.strip}>
                {ROLL.map((f, i) => {
                  const inVault = vaulted && f.priv
                  const isCurrent = !inVault && !atEnd && activeRoll[pos] === f
                  return (
                    <span
                      key={i}
                      className={`${styles.thumbTile} ${
                        f.priv ? styles.thumbPriv : ""
                      } ${inVault ? styles.inVault : ""} ${
                        isCurrent ? styles.thumbCurrent : ""
                      }`}
                      style={{ ["--i" as any]: i }}
                    >
                      <span
                        className={styles.thumbImg}
                        style={{ backgroundImage: `url(${f.src})` }}
                      />
                      {f.priv && (
                        <span className={styles.thumbLock} aria-hidden="true" />
                      )}
                    </span>
                  )
                })}
              </div>
            </div>

            {/* the Vault dock beneath the phone — where the private ones live */}
            <div
              className={`${styles.vaultDock} ${vaulted ? styles.dockOn : ""}`}
              aria-hidden="true"
            >
              <span className={styles.dockIcon}>
                <svg viewBox="0 0 24 24" width="16" height="16">
                  <rect
                    x="3.5"
                    y="7"
                    width="17"
                    height="13"
                    rx="2.6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                  />
                  <path
                    d="M7.5 7V5.6A4.5 4.5 0 0 1 16.5 5.6V7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  />
                  <circle cx="12" cy="13.4" r="1.7" fill="currentColor" />
                  <path
                    d="M12 15v2.1"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <span className={styles.dockText}>
                <span className={styles.dockLabel}>Your Vault</span>
                <span className={styles.dockSub}>
                  {vaulted
                    ? `${vaultCount} private photos — kept out of the roll`
                    : "Empty — private photos are loose in the roll"}
                </span>
              </span>
              <span className={styles.dockCount}>
                {vaulted ? vaultCount : 0}
              </span>
            </div>
          </div>

          {/* status */}
          <span className={styles.status} aria-live="polite">
            <span className={styles.statusDot} aria-hidden="true" />
            {status}
          </span>

          {/* the decisive affordance — the Vault switch */}
          <button
            className={`${styles.vaultBtn} ${vaulted ? styles.vaultOn : ""}`}
            onClick={toggleVault}
            aria-pressed={vaulted}
          >
            <span className={styles.vaultSwitch} aria-hidden="true">
              <span className={styles.vaultKnob} />
            </span>
            <span className={styles.vaultText}>
              {vaulted
                ? "Private photos are in the Vault"
                : "Keep private photos in the Vault"}
            </span>
          </button>

          <div className={styles.replayRow}>
            <button className={styles.replay} onClick={rewind}>
              <svg viewBox="0 0 24 24" width="14" height="14">
                <path
                  d="M4.5 12a7.5 7.5 0 1 1 2.2 5.3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                />
                <path
                  d="M4.2 17.6l.4-3.6 3.6.4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Hand it over again
            </button>
          </div>
        </section>

        {/* ── the idea, in three real features ───────────────────────────── */}
        <ul className={styles.features}>
          <li className={styles.featHead}>What keeps them out of the roll</li>
          {FEATURES.map((f, i) => (
            <li
              key={f.key}
              className={styles.feature}
              style={{ ["--d" as any]: i }}
            >
              <span className={styles.featIcon} aria-hidden="true">
                <f.Icon width={18} height={18} />
              </span>
              <span className={styles.featText}>
                <span className={styles.featName}>{f.name}</span>
                <span className={styles.featLine}>{f.line}</span>
              </span>
            </li>
          ))}
        </ul>

        <p className={styles.assure}>
          <span className={styles.assureMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="13" height="13">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <path
                d="M9.2 12.1l2 2 3.6-3.9"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          Moving a photo to the Vault only takes it out of the Camera Roll —
          it’s kept safe, backed up, and never deleted. You can move it back any
          time.
        </p>

        {/* ── pricing: yearly featured, monthly small & secondary ────────── */}
        <div className={styles.plans}>
          <button
            className={`${styles.yearly} ${
              plan === "yearly" ? styles.selected : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            <span className={styles.yearlyGlow} aria-hidden="true" />
            <span className={styles.yearlyHead}>
              <span className={styles.yearlyName}>
                Vault for a year
                <span className={styles.trialPill}>
                  {trialDays}-day free trial
                </span>
              </span>
              {savingsPct > 0 && (
                <span className={styles.saveTag}>Save {savingsPct}%</span>
              )}
            </span>
            <span className={styles.yearlyPriceRow}>
              <span className={styles.yearlyPrice}>{yearlyPrice}</span>
              <span className={styles.yearlyUnit}>/ year</span>
              <span className={styles.yearlyPerMo}>≈ {yearlyPerMonth}/mo</span>
              <span className={styles.check} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="13" height="13">
                  <path
                    d="M5 12.5l4.2 4.2L19 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </span>
          </button>

          <button
            className={`${styles.monthly} ${
              plan === "monthly" ? styles.monthlySel : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span>Or month-to-month</span>
            <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
          </button>
        </div>

        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaLabel}>
            {plan === "yearly"
              ? `Start ${trialDays}-day free trial`
              : "Get Premium"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `Then ${yearlyPrice}/year · cancel anytime`
              : `${monthlyPrice}/month · cancel anytime`}
          </span>
        </button>

        <div className={styles.footer}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span className={styles.fdot}>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span className={styles.fdot}>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default HandoffPaywall
